import React, { useState, useEffect } from "react";
import {
  Grid2 as Grid,
  Typography,
  Button,
  Box,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  StoreMallDirectory as StoreIcon,
  Groups as GroupsIcon,
  Web as WebIcon
} from "@mui/icons-material";
import { useFormContext, useWatch } from "react-hook-form";

import { Form } from "@shared/components";
import { ProductMetadata } from "../../types/product";

export function ProductForm() {
  const { setValue, getValues, control } = useFormContext();
  const [metadataKeys, setMetadataKeys] = useState<string[]>([]);

  const watchedMetadata = useWatch({ control, name: "metadata" }) as Record<string, unknown> | undefined;

  useEffect(() => {
    const keys = watchedMetadata ? Object.keys(watchedMetadata) : [];
    setMetadataKeys((prev) => {
      if (prev.length === 0) return keys;

      const prevSet = new Set(prev);
      const newSet = new Set(keys);

      let next = prev.filter((k) => newSet.has(k));

      const added = keys.filter((k) => !prevSet.has(k));
      if (added.length > 0) {
        next = [...added, ...next];
      }

      return next;
    });
  }, [watchedMetadata]);

  const addMetadata = () => {
    const newKey = `new_metadata_${Date.now()}`;
    const newMetadata: ProductMetadata = {
      key: "",
      value: "",
      type: "string",
      obrigatorio: false,
      default_value: false
    };

    setValue(`metadata.${newKey}`, newMetadata);
    setMetadataKeys(prev => [newKey, ...prev]);
  };

  const removeMetadata = (key: string) => {
    const currentMetadata = getValues("metadata") || {};
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [key]: _, ...rest } = currentMetadata;
    setValue("metadata", rest);
    setMetadataKeys(prev => prev.filter(k => k !== key));
  };

  const metadataTypes = [
    { label: "Texto", value: "string" },
    { label: "Área de texto", value: "textArea" },
    { label: "Verdadeiro/Falso", value: "boolean" },
    { label: "Número", value: "number" },
    { label: "Data", value: "date" },
    { label: "Imagem", value: "image" },
    { label: "Cor", value: "color" },
    { label: "Seleção de Categorias Bookstore", value: "select_categories_library" },
  ];

  return (
    <Box sx={{ pr: 1 }}>
      <Grid container spacing={3}>
        {/* Seção de Informações Básicas */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardContent sx={{ pb: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Form.Input
                    name="name"
                    label="Nome do Produto"
                    required
                    placeholder="Ex: Produto Premium"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Seção de Configurações */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardContent sx={{ pb: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
                    <StoreIcon fontSize="small" color="action" />
                    <Form.Switch
                      name="is_bookstore"
                      label="Bookstore"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
                    <GroupsIcon fontSize="small" color="action" />
                    <Form.Switch
                      name="is_superapp"
                      label="Super App"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
                    <WebIcon fontSize="small" color="action" />
                    <Form.Switch
                      name="create_landing"
                      label="Criar Landing"
                    />
                  </Box>
                </Grid>


              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Seção de Metadata */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardContent sx={{ pb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" color="primary">
                    Metadata Personalizada
                  </Typography>
                  <Chip
                    label={`${metadataKeys.length} item${metadataKeys.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addMetadata}
                  sx={{ borderRadius: 2 }}
                >
                  Adicionar
                </Button>
              </Box>

              {metadataKeys.length > 0 && (
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Configure propriedades personalizadas para seu produto
                </Typography>
              )}

              <Stack spacing={2}>
                {metadataKeys.map((key, index) => (
                  <Paper
                    key={key}
                    elevation={1}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: 'primary.main',
                        transition: 'border-color 0.2s'
                      }
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                      <Typography variant="subtitle2" color="primary">
                        #{index + 1}
                      </Typography>
                      <IconButton
                        onClick={() => removeMetadata(key)}
                        size="small"
                        color="error"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'white'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Form.Input
                          name={`metadata.${key}.key`}
                          label="Key"
                          size="small"
                          required
                          placeholder="Ex: category"
                          onChange={(e) => {
                            const value = e.target.value.replace(/\s/g, '');
                            e.target.value = value;
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Form.Input
                          name={`metadata.${key}.value`}
                          label="Label"
                          size="small"
                          required
                          placeholder="Ex: Categoria"
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Form.Select
                          name={`metadata.${key}.type`}
                          label="Tipo"
                          options={metadataTypes}
                          size="small"
                          required
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 3 }} display="flex" alignItems="center">
                        <Form.Switch
                          name={`metadata.${key}.obrigatorio`}
                          label="Obrigatório"
                          size="small"
                        />
                      </Grid>

                      {(watchedMetadata as any)?.[key]?.type === 'boolean' && (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} display="flex" alignItems="center">
                          <Form.Switch
                            name={`metadata.${key}.default_value`}
                            label="Valor padrão"
                            size="small"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                ))}
              </Stack>

              {metadataKeys.length === 0 && (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: 'grey.50',
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: 'grey.300'
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Nenhuma metadata adicionada
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clique em "Adicionar" para começar a personalizar seu produto
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
